import {
  AdminDatePickerModal,
  AdminHeader,
  AdminSelect,
  AdminSpinner,
  Div,
  H3,
  H5,
  Img,
  P,
  SearchInput,
  Tab,
  TableHeader,
  findAllPayments,
  handleSetUrlSearchParams,
  moneyAndCoins,
  renderDateLabelWithToday,
  retryPayment,
  setTimeStartEnd,
  setTimeStartEndMonth,
  useClientPayments,
  reportError,
} from '@vgrubs/components';
import { ClientPaymentStatus } from 'components/src/types/clientPayments.types';
import { WalletReportType } from 'components/src/types/wallet.types';
import debouce from 'lodash.debounce';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSearchParams } from 'react-router-dom';
import {
  canRetryPayment,
  clientPaymentsColumns,
} from './components/ClientPaymentCells';
import { ClientPaymentComponent } from './components/ClientPaymentComponent';

type OptionValue = ClientPaymentStatus | 'allStatuses';

const statusOptions: {
  name: string;
  value: OptionValue;
}[] = [
  { name: 'All Statuses', value: 'allStatuses' },
  { name: 'Initiated', value: ClientPaymentStatus.initiated },
  { name: 'Failed', value: ClientPaymentStatus.failed },
  { name: 'Completed', value: ClientPaymentStatus.completed },
  { name: 'Cancelled', value: ClientPaymentStatus.cancelled },
  { name: 'Charged Back', value: ClientPaymentStatus.chargedBack },
];

const typeOptions = (
  <optgroup label="Select Status">
    {statusOptions.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </optgroup>
);

export const ClientPayments = () => {
  const today = new Date();

  const INITIAL_DATE = [
    setTimeStartEndMonth(today, 'start'),
    setTimeStartEndMonth(today, 'today'),
  ];

  const [dateInput, setDateInput] = useState<(Date | string)[]>(INITIAL_DATE);
  // const { clientPaymentsTab, setClientPaymentsTab } = useUIState(
  //   (state) => state
  // );

  const { clientPayments, setClientPayments, setRetriedPayment } =
    useClientPayments((state) => state);

  const [loading, setLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const status = (searchParams.get('status') ??
    null) as ClientPaymentStatus | null;
  const type = (searchParams.get('type') ?? 'vGrubs') as WalletReportType;
  const from = searchParams.get('from') ?? INITIAL_DATE[0];
  const to = searchParams.get('to') ?? INITIAL_DATE[1];
  const page = Number(searchParams.get('page')) ?? 1;
  const initialQuery = searchParams.get('query') || '';
  const [inputValue, setInputValue] = useState(initialQuery);
  const [query, setQuery] = useState<string | null>(initialQuery || null);

  const limit = 20;
  const [hasMore, setHasMore] = useState(true);
  const [ref, inView] = useInView();

  const isTypeVGrubs = type === 'vGrubs';

  const setDate = (date: [string, string]) => {
    handleSetUrlSearchParams('from', date[0], setSearchParams);
    handleSetUrlSearchParams('to', date[1], setSearchParams);
  };

  const setPage = (page: number | string) => {
    handleSetUrlSearchParams('page', page, setSearchParams, true);
  };

  const setStatus = (value: OptionValue) => {
    if (value === 'allStatuses')
      return handleSetUrlSearchParams('status', null, setSearchParams);
    handleSetUrlSearchParams('status', value, setSearchParams);
  };

  const setType = (value: WalletReportType) => {
    handleSetUrlSearchParams('type', value, setSearchParams);
  };

  const debouncedSearch = useMemo(
    () => debouce((value: string | null) => setQuery(value), 500),
    []
  );

  const handleDateChange = (date: Date[] | string[]) => {
    setDateInput(date);
  };

  const handleDateApply = () => {
    setClientPayments([]);

    if (dateInput.length < 2) {
      if (isTypeVGrubs) {
        setDate([
          setTimeStartEndMonth(dateInput[0], 'start'),
          setTimeStartEndMonth(dateInput[0], 'end'),
        ]);
      } else {
        setDate([
          setTimeStartEnd(dateInput[0], 'start'),
          setTimeStartEnd(dateInput[0], 'end'),
        ]);
      }
      setDateInput([dateInput[0], dateInput[0]] as Date[]);
    } else {
      if (isTypeVGrubs) {
        setDate([
          setTimeStartEndMonth(dateInput[0], 'start'),
          setTimeStartEndMonth(dateInput[1], 'end'),
        ]);
      } else {
        setDate([
          setTimeStartEnd(dateInput[0], 'start'),
          setTimeStartEnd(dateInput[1], 'end'),
        ]);
      }
    }
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value || null);
    handleSetUrlSearchParams('query', value, setSearchParams);
  };

  async function getOrders(hasMore, page, isFilter) {
    if (!hasMore) return;

    try {
      setLoading(true);

      const { data } = await findAllPayments({
        page,
        limit,
        status,
        from,
        to,
        type,
        query,
      });

      if (data.data.length === limit) {
        setPage(Number(page) + 1);
        setHasMore(true);
      } else {
        setHasMore(false);
      }

      if (isFilter) {
        setClientPayments(data.data);
      } else {
        setClientPayments([...clientPayments, ...data.data]);
      }
      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  async function handleRetryPayment(id) {
    try {
      setLoading(true);
      const { data } = await retryPayment(id);
      setRetriedPayment(data.data);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    getOrders(true, 1, true);

    return () => {
      setClientPayments([]);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, status, query, type]);

  useEffect(() => {
    if (inView && clientPayments.length > 0) {
      getOrders(hasMore, page, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const columns = clientPaymentsColumns(clientPayments.some(canRetryPayment));

  return (
    <>
      {loading && <AdminSpinner isRootPage />}
      <AdminHeader title={'Client Payments'}>
        <Div $flex $gap="20px" $gapSM="20px">
          {/* {type === WalletReportType.vGrubs ? (
            <AdminDatePickerModal
              mode="range"
              value={dateInput}
              onChange={handleDateChange}
              click={handleDateApply}
              monthPicker
            />
          ) : ( */}
          <AdminDatePickerModal
            mode="range"
            value={dateInput}
            onChange={handleDateChange}
            click={handleDateApply}
          />
          {/* )} */}
          <SearchInput
            value={inputValue}
            maxWidth="350px"
            maxWidthSM="130px"
            placeholder="Search"
            onChange={onSearchChange}
          />
        </Div>
      </AdminHeader>
      <Div $mobile>
        <H5 $mt="32" $mtSM="20" $mb="0">
          {renderDateLabelWithToday(from, to)}
        </H5>
      </Div>
      <Div
        $flex
        $alignCenter
        $between
        $columnSM
        $gap="32px"
        $gapSM="20px"
        $p="32px 0"
        $pSM="20px 0"
      >
        <Div $flex $gap="24px" $gapSM="16px" $width="100%">
          <Tab
            $widthSM="calc(50% - 8px)"
            selected={type === WalletReportType.vGrubs}
            onClick={() => setType(WalletReportType.vGrubs)}
          >
            vGrubs
          </Tab>

          <Tab
            $widthSM="calc(50% - 8px)"
            selected={type === WalletReportType.vDrive}
            onClick={() => setType(WalletReportType.vDrive)}
          >
            vDrive
          </Tab>
        </Div>

        <Div $width="300px" $widthSM="100%">
          <AdminSelect
            value={status || ''}
            options={typeOptions}
            onChange={({ target }) => {
              setStatus(target.value as OptionValue);
            }}
          />
        </Div>
      </Div>
      <Div
        $flex
        $alignCenter
        $between
        $columnSM
        $gap="32px"
        $gapSM="24px"
        $mb="32"
        $mbSM="20"
      >
        <Div $desktop>
          <H5 $mb="0">{renderDateLabelWithToday(from, to)}</H5>
        </Div>
      </Div>
      {clientPayments.length > 0 && (
        <TableHeader padding="0 24px" gap="20px" columns={columns} />
      )}
      {clientPayments.map((payment) => {
        return (
          <ClientPaymentComponent
            key={payment.id}
            payment={payment}
            retryPayment={handleRetryPayment}
            columns={columns}
          />
        );
      })}

      {!loading && clientPayments.length < 1 && (
        <Div $flex $column $alignCenter $mt="150">
          <Img alt="" $width="200px" src={moneyAndCoins} $mb={24} />
          <H3 $mb="24" $center>
            No Client Payments
          </H3>
          <P $mb="32" $center>
            No results found for the selected filters.
          </P>
        </Div>
      )}

      <div ref={ref}></div>
    </>
  );
};
